import React, { useState, useEffect } from "react";
import {
	Typography,
	Paper,
	Container,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	Divider,
	Box,
	Card,
	CardContent,
	CircularProgress,
	Alert,
	Snackbar,
	Chip,
	Button,
} from "@mui/material";
import {
	Download,
	VideoLibrary,
	CalendarToday,
	Error as ErrorIcon,
	Refresh,
	PlayArrow,
	Edit,
} from "@mui/icons-material";
import Api from "../Api/Api.json";
import SidebarNav from "./SidebarNav";
import { useNavigate } from "react-router-dom";
import SimulatedEditor from "./SimulatedEditor";

const CreatedVideoList = () => {
	const [videoList, setVideoList] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedVideo, setSelectedVideo] = useState(null);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const navigate = useNavigate();

	const base_url = Api.MAGICVERSE_USER_API;

	useEffect(() => {
		fetchAuthVideos();
	}, []);

	const fetchAuthVideos = async () => {
		setLoading(true);
		setError(null);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const body = {
				task: "getCompletedVideoList",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			// Format the data as objects rather than strings for better handling
			const formattedData = data.map((item) => ({
				topic: item.topic,
				date: item.created_on,
				formattedDate: formatDate(item.created_on),
			}));

			setVideoList(formattedData);
		} catch (err) {
			console.log(err.message);
			setError("Failed to fetch video list. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const handleDownloadVideo = async (date) => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const body = {
				task: "getVideoDownloadLink",
				date: date,
			};

			setSnackbar({
				open: true,
				message: "Preparing your download...",
				severity: "info",
			});

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const presigned_url = await response.json();

			if (presigned_url) {
				const link = document.createElement("a");
				link.href = presigned_url;
				link.download = "";
				link.target = "_blank";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				setSnackbar({
					open: true,
					message: "Download started successfully!",
					severity: "success",
				});
			}
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to download video. Please try again.",
				severity: "error",
			});
		}
	};

	const handleEditVideo = async (date, created_on) => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const body = {
				task: "getVideoDownloadLink",
				date: date,
			};

			setSnackbar({
				open: true,
				message: "Preparing video for editing...",
				severity: "info",
			});

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const presigned_url = await response.json();

			if (presigned_url) {
				setSelectedVideo({ url: presigned_url, date: date });
			}
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to prepare video for editing. Please try again.",
				severity: "error",
			});
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	return (
		<div className="bg-gray-50 min-h-screen">
			{selectedVideo ? (
				<Container maxWidth="md" className="py-8">
					<Button
						variant="outlined"
						onClick={() => setSelectedVideo(null)}
						style={{ marginBottom: '20px' }}
					>
						Back to Video List
					</Button>
					<SimulatedEditor presignedUrl={selectedVideo.url} date={selectedVideo.date} />
				</Container>
			) : (
				<Container maxWidth="md" className="py-8">
					<Paper elevation={3} className="overflow-hidden rounded-lg">
						{/* Header with gradient background */}
						<Box className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
							<Box className="flex items-center mb-2">
								<VideoLibrary fontSize="large" className="mr-3" />
								<Typography variant="h4" className="font-bold">
									My Created Videos
								</Typography>
							</Box>
							<Typography variant="subtitle1">
								Access and download your generated video content
							</Typography>
						</Box>

						{/* Content section */}
						<Box className="p-6">
							<Box className="flex justify-between items-center mb-6">
								<Typography
									variant="body1"
									className="text-gray-600"
								>
									Click on any video to download it to your device
								</Typography>

								<Button
									startIcon={<Refresh />}
									variant="outlined"
									color="primary"
									size="small"
									onClick={fetchAuthVideos}
									disabled={loading}
									className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
								>
									Refresh
								</Button>
							</Box>

							{loading ? (
								<Box className="flex justify-center items-center py-16">
									<CircularProgress
										color="primary"
										className="text-indigo-600"
									/>
								</Box>
							) : error ? (
								<Alert
									severity="error"
									className="mb-4"
									icon={<ErrorIcon />}
									variant="filled"
								>
									{error}
								</Alert>
							) : videoList.length === 0 ? (
								<Card
									variant="outlined"
									className="bg-gray-50 text-center py-12 border border-gray-200"
								>
									<CardContent>
										<VideoLibrary
											className="text-gray-300 mb-3"
											style={{ fontSize: 60 }}
										/>
										<Typography
											variant="h6"
											className="text-gray-600 mb-2 font-medium"
										>
											No videos created yet
										</Typography>
										<Typography
											variant="body2"
											className="text-gray-500 mb-4"
										>
											Create your first video from the
											dashboard
										</Typography>
										<Button
											variant="contained"
											color="primary"
											href="/dashboard"
											className="mt-2 bg-indigo-600 hover:bg-indigo-700"
											startIcon={<PlayArrow />}
										>
											Create a Video
										</Button>
									</CardContent>
								</Card>
							) : (
								<>
									<List className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
										{videoList.map((item, index) => (
											<React.Fragment key={index}>
												<ListItem
													button={false}
													onClick={() =>
														handleDownloadVideo(
															item.date
														)
													}
													className="hover:bg-indigo-50 py-3 transition-colors duration-200 cursor-pointer"
												>
													<ListItemText
														primary={
															<Typography
																variant="subtitle1"
																className="font-medium text-gray-800"
															>
																{item.topic}
															</Typography>
														}
														secondary={
															<Box className="flex items-center mt-1">
																<CalendarToday
																	fontSize="small"
																	className="text-gray-400 mr-1"
																	style={{
																		fontSize:
																			"14px",
																	}}
																/>
																<Typography
																	variant="body2"
																	className="text-gray-500"
																>
																	{
																		item.formattedDate
																	}
																</Typography>
															</Box>
														}
													/>
													<ListItemSecondaryAction>
														<IconButton
															edge="end"
															aria-label="edit"
															onClick={(e) => {
																e.stopPropagation();
																handleEditVideo(item.date, item.created_on);
															}}
															className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 mr-2"
														>
															<Edit />
														</IconButton>
														<IconButton
															edge="end"
															aria-label="download"
															onClick={(e) => {
																e.stopPropagation();
																handleDownloadVideo(
																	item.date
																);
															}}
															className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
														>
															<Download />
														</IconButton>
													</ListItemSecondaryAction>
												</ListItem>
												{index < videoList.length - 1 && (
													<Divider component="li" />
												)}
											</React.Fragment>
										))}
									</List>

									<Box className="mt-8 flex justify-center">
										<Chip
											icon={
												<VideoLibrary className="text-indigo-600" />
											}
											label={`Total Videos: ${videoList.length}`}
											variant="outlined"
											className="px-4 py-3 border-indigo-200"
										/>
									</Box>
								</>
							)}
						</Box>
					</Paper>
				</Container>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
					className="shadow-lg"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default CreatedVideoList;
